import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ReportService } from '../../../../core/api/report.service';
import { ToastrService } from 'ngx-toastr';

export interface ReportExcelContext {
  find: 'all' | 'reimburse' | 'cashadvance';
  page: number;
  limit: number;
  search?: string;
  status?: string | null;
}

@Component({
  selector: 'app-generate-excel',
  standalone: true,
  imports: [
    MatRadioModule,
    MatDividerModule,
    MatSelectModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './generate-excel.component.html',
  styleUrl: './generate-excel.component.scss',
})
export class GenerateExcelComponent implements OnInit {
  form: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<GenerateExcelComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportExcelContext,
    private fb: FormBuilder,
    private service: ReportService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      options: [null, Validators.required],
      startDate: [null],
      endDate: [null],
      type: ['all'],
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  generate(): void {
    const options = this.form.get('options')?.value;

    if (!options) {
      this.toastrService.error('Choose generate option!');
      return;
    }

    const context = this.data ?? {
      find: 'all',
      page: 1,
      limit: 20,
      search: '',
      status: 'all',
    };

    const payload: any = {
      find: context.find ?? 'all',
      page: context.page ?? 1,
      limit: context.limit ?? 20,
      search: context.search ?? '',
      status: context.status ?? null,
      exportAll: false,
      startDate: null,
      endDate: null,
    };

    if (options == 1) {
      payload.exportAll = false;
    } else if (options == 2) {
      const startDate = this.form.get('startDate')?.value;
      const endDate = this.form.get('endDate')?.value;

      if (!startDate || !endDate) {
        this.toastrService.error('Start date and end date are required');
        return;
      }

      payload.exportAll = true;
      payload.startDate = startDate;
      payload.endDate = endDate;
    } else if (options == 3) {
      payload.exportAll = true;
      payload.search = '';
      payload.status = null;
      payload.startDate = null;
      payload.endDate = null;
    }

    this.service
      .generate(payload)
      .then((item) => {
        if (item?.base64 && item?.fileName) {
          this.downloadBase64File(item.base64, item.fileName);
          this.toastrService.success('Excel file downloaded successfully');
          this.dialogRef.close(true);
          return;
        }

        this.toastrService.error('Failed to generate Excel file');
      })
      .catch((err) => {
        this.toastrService.error(err?.message ?? 'Failed to generate Excel file');
      });
  }

  private downloadBase64File(base64: string, fileName: string): void {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
